"use client";

import * as React from "react";
import {
    Button,
    Card,
    Title,
    Text,
    TextInput,
    Group,
    Stack,
    Badge,
    Code,
    Image,
    Center,
    LoadingOverlay,
    useMantineColorScheme
} from "@mantine/core";
import { useAuth } from "../../hooks/use-auth";
import { setupMFA, enableMFA, disableMFA } from "../../apis/auth";
import type { UserMfaSetup } from "../../lib/types";

export interface MFASettingsProps {
    hideAdminMfa?: boolean;
}

export function MFASettings({ hideAdminMfa }: MFASettingsProps = {}) {
    const { token, refreshUser, user } = useAuth();
    const [setupData, setSetupData] = React.useState<UserMfaSetup | null>(null);
    const [code, setCode] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [enablingApp, setEnablingApp] = React.useState<"web" | "admin" | null>(null);
    const { colorScheme } = useMantineColorScheme();

    const handleStartSetup = async (app: "web" | "admin") => {
        if (!token) return;
        setIsLoading(true);
        setError(null);
        try {
            const data = await setupMFA(token);
            setSetupData(data);
            setEnablingApp(app);
        } catch (err) {
            setError("Failed to initialize MFA setup.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleEnable = async () => {
        if (!token || !setupData || !enablingApp) return;
        setIsLoading(true);
        setError(null);
        try {
            const { success } = await enableMFA(token, setupData.secret, code, enablingApp);
            if (success) {
                await refreshUser();
                setEnablingApp(null);
                setSetupData(null);
                setCode("");
            } else {
                setError("Invalid verification code.");
            }
        } catch (err) {
            setError("Failed to enable MFA.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDisable = async (app: "web" | "admin") => {
        if (!token) return;
        if (
            !confirm(
                `Are you sure you want to disable MFA for ${app === "admin" ? "Admin Console" : "Web App"}?`
            )
        )
            return;

        setIsLoading(true);
        setError(null);
        try {
            const { success } = await disableMFA(token, app);
            if (success) {
                await refreshUser();
            }
        } catch (err) {
            setError("Failed to disable MFA.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Card 
            withBorder
            style={{
                backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'var(--mantine-color-white)',
                borderColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : undefined
            }}
        >
            <LoadingOverlay visible={isLoading} />
            <Card.Section 
                withBorder 
                inheritPadding 
                py="xs"
                style={{ borderColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : undefined }}
            >
                <Title order={4}>Multi-Factor Authentication (MFA)</Title>
                <Text size="sm" c="dimmed">
                    Manage security settings for different parts of the platform.
                </Text>
            </Card.Section>
            <Card.Section inheritPadding py="md">
                <Stack gap="xl">
                    {error && (
                        <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
                            {error}
                        </div>
                    )}

                    {!enablingApp ? (
                        <>
                            {/* Web App MFA */}
                            <Group justify="space-between" align="flex-start">
                                <div>
                                    <Text fw={500}>Web Application MFA</Text>
                                    <Text size="sm" c="dimmed">
                                        Secure access to dashboards and reports.
                                    </Text>
                                    <div className="mt-1">
                                        {user?.mfaEnabledWebApp ? (
                                            <Badge color="green" variant="light">
                                                Enabled
                                            </Badge>
                                        ) : (
                                            <Badge color="gray" variant="light">
                                                Disabled
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                                {user?.mfaEnabledWebApp ? (
                                    <Button
                                        color="red"
                                        variant="outline"
                                        onClick={() => handleDisable("web")}
                                        loading={isLoading}
                                    >
                                        Disable
                                    </Button>
                                ) : (
                                    <Button 
                                        variant="filled" 
                                        color="black"
                                        c="white" 
                                        onClick={() => handleStartSetup("web")} 
                                        loading={isLoading}
                                    >
                                        Setup Web MFA
                                    </Button>
                                )}
                            </Group>
                            {/* Admin Console MFA */}
                            {!hideAdminMfa && (
                                <Group justify="space-between" align="flex-start">
                                    <div>
                                        <Text fw={500}>Admin Console MFA</Text>
                                        <Text size="sm" c="dimmed">
                                            Secure access to system settings and user management.
                                        </Text>
                                        <div className="mt-1">
                                            {user?.mfaEnabledAdminConsole ? (
                                                <Badge color="green" variant="light">
                                                    Enabled
                                                </Badge>
                                            ) : (
                                                <Badge color="gray" variant="light">
                                                    Disabled
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    {user?.mfaEnabledAdminConsole ? (
                                        <Button
                                            color="red"
                                            variant="outline"
                                            onClick={() => handleDisable("admin")}
                                            loading={isLoading}
                                        >
                                            Disable
                                        </Button>
                                    ) : (
                                        <Button 
                                            variant="filled" 
                                            color="black"
                                            c="white" 
                                            onClick={() => handleStartSetup("admin")} 
                                            loading={isLoading}
                                        >
                                            Setup Admin MFA
                                        </Button>
                                    )}
                                </Group>
                            )}
                        </>
                    ) : (
                        <Stack gap="md">
                            <Group align="center" gap="xs">
                                <Title order={5}>Enabling MFA for {enablingApp === "admin" ? "Admin Console" : "Web App"}</Title>
                            </Group>
                            <div className="rounded-md bg-gray-50 p-4 flex flex-col items-center">
                                {setupData?.qrCode ? (
                                    <Image
                                        src={setupData.qrCode}
                                        alt="MFA QR Code"
                                        w={160}
                                        h={160}
                                        className="mb-4 bg-white p-2 rounded shadow-sm"
                                    />
                                ) : (
                                    <Center w={160} h={160} className="mb-4 bg-gray-100 rounded">
                                        <Text size="xs" c="dimmed">Generating QR...</Text>
                                    </Center>
                                )}
                                <Text size="sm" ta="center" fw={500} mb="xs">
                                    Scan this QR code with your authenticator app.
                                </Text>
                                <Text size="xs" ta="center" c="dimmed" mb="md" maw={240}>
                                    If you can't scan the QR code, enter this secret key manually:
                                </Text>
                                <Code block fz="xs" fw={700}>
                                    {setupData?.secret}
                                </Code>
                            </div>

                            <TextInput
                                label="Verification Code"
                                placeholder="000000"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                maxLength={6}
                                disabled={isLoading}
                                autoFocus
                            />

                            <Group>
                                <Button 
                                    variant="filled" 
                                    color="black"
                                    c="white" 
                                    onClick={handleEnable} 
                                    disabled={isLoading || code.length !== 6}
                                >
                                    Verify and Enable
                                </Button>
                                <Button
                                    variant="outline"
                                    color="gray"
                                    onClick={() => setEnablingApp(null)}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </Button>
                            </Group>
                        </Stack>
                    )}
                </Stack>
            </Card.Section>
        </Card>
    );
}
